import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';

// Generate a unique session ID
const generateSessionId = () => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Get or create session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('visitor_session_id');
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem('visitor_session_id', sessionId);
  }
  return sessionId;
};

// Get visitor IP (simplified - in production you might want to use a service)
const getVisitorIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn('Could not fetch IP address:', error);
    return 'unknown';
  }
};

// Track page visit
const trackPageVisit = async (pageData) => {
  try {
    const visitorIP = await getVisitorIP();
    const sessionId = getSessionId();
    
    const visitData = {
      page_path: pageData.path,
      page_title: pageData.title,
      visitor_ip: visitorIP,
      user_agent: navigator.userAgent,
      referrer: document.referrer || null,
      session_id: sessionId,
      visit_date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
      visit_time: new Date().toISOString()
    };

    const { error } = await supabase
      .from('visitor_stats')
      .insert([visitData]);

    if (error) {
      console.error('Error tracking page visit:', error);
    }
  } catch (error) {
    console.error('Error in trackPageVisit:', error);
  }
};

// Custom hook for visitor tracking
export const useVisitorTracking = () => {
  const location = useLocation();

  const trackVisit = useCallback(async () => {
    const pageData = {
      path: location.pathname,
      title: document.title
    };

    // Add a small delay to ensure page is fully loaded
    setTimeout(() => {
      trackPageVisit(pageData);
    }, 1000);
  }, [location.pathname]);

  useEffect(() => {
    trackVisit();
  }, [trackVisit]);

  return { trackVisit };
};

// Hook for getting visitor statistics
export const useVisitorStats = () => {
  const getDailyStats = async (date = null) => {
    try {
      let query = supabase
        .from('daily_visitor_stats')
        .select('*')
        .order('visit_date', { ascending: false })
        .limit(30); // Last 30 days

      if (date) {
        query = query.eq('visit_date', date);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching daily stats:', error);
      return [];
    }
  };

  const getMonthlyStats = async () => {
    try {
      const { data, error } = await supabase
        .from('monthly_visitor_stats')
        .select('*')
        .order('month', { ascending: false })
        .limit(12); // Last 12 months

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching monthly stats:', error);
      return [];
    }
  };

  const getTotalStats = async () => {
    try {
      const { data, error } = await supabase
        .from('total_visitor_stats')
        .select('*')
        .single();

      if (error) throw error;
      return data || {};
    } catch (error) {
      console.error('Error fetching total stats:', error);
      return {};
    }
  };

  const getTodayStats = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('daily_visitor_stats')
        .select('*')
        .eq('visit_date', today)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
      return data || { daily_visits: 0, unique_visitors: 0, unique_ips: 0 };
    } catch (error) {
      console.error('Error fetching today stats:', error);
      return { daily_visits: 0, unique_visitors: 0, unique_ips: 0 };
    }
  };

  const getThisMonthStats = async () => {
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1; // 0-based month
      const lastDay = new Date(year, month, 0).getDate(); // Get last day of current month
      
      const thisMonth = `${year}-${month.toString().padStart(2, '0')}`;
      const { data, error } = await supabase
        .from('monthly_visitor_stats')
        .select('*')
        .gte('month', `${thisMonth}-01`)
        .lte('month', `${thisMonth}-${lastDay.toString().padStart(2, '0')}`)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data || { monthly_visits: 0, unique_visitors: 0, unique_ips: 0 };
    } catch (error) {
      console.error('Error fetching this month stats:', error);
      return { monthly_visits: 0, unique_visitors: 0, unique_ips: 0 };
    }
  };

  return {
    getDailyStats,
    getMonthlyStats,
    getTotalStats,
    getTodayStats,
    getThisMonthStats
  };
};

export default useVisitorTracking;
